<template>
  <section class="height-100-p">
    <el-table
      ref="searchSummaryInvoicesTable"
      :data="invoices.list"
      v-loading="invoices.loadingSearch"
      id="invoices-data-list"
      size="mini"
      tooltip-effect="dark"
      class="dark-blue-table"
      :empty-text="$t('common.notAbleToFindRecords')"
      :select-on-indeterminate="false"
      sortable
      stripe
      @select-all="handleSelectAllToBulkPrint"
      @select="handleSelectSingleToBulkPrint"
      @sort-change="handleSortChange"
      :default-sort="{prop: 'number', order: 'ascending'}"
    >
      <el-table-column
        prop="number"
        :label="$t('invoices.invoiceNum')"
        :min-width="10"
        sortable="custom"
        align="center">
      </el-table-column>
      <el-table-column
        prop="date"
        :label="$t('invoices.date')"
        :min-width="10"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          {{ scope.row.date | moment("MM/DD/YYYY") }}
        </template>
      </el-table-column>
      <el-table-column
        prop="type"
        :label="$t('invoices.type')"
        :min-width="7"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="getNameOfInvoiceType(scope.row.type)" placement="top">
            <span> {{ getNameOfInvoiceType(scope.row.type) }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="billToAccountName"
        :label="$t('invoices.billTo')"
        :min-width="15"
        sortable="custom"
        align="left"
        header-align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.billToAccountName + ' - ' + scope.row.billToAccountNumber" placement="top">
            <span> {{ scope.row.billToAccountName }} - {{ scope.row.billToAccountNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="consigneeAccountName"
        :label="$t('outbound.units.consignee')"
        :min-width="15"
        sortable="custom"
        align="left"
        header-align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.consigneeAccountName + ' - ' + scope.row.consigneeAccountNumber" placement="top">
            <span> {{ scope.row.consigneeAccountName }} - {{ scope.row.consigneeAccountNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="deliverToAccountName"
        :label="$t('invoices.deliverTo')"
        :min-width="15"
        sortable="custom"
        align="left"
        header-align="center">
        <template slot-scope="scope">
          <el-tooltip class="item" effect="dark" :content="scope.row.deliverToAccountName + ' - ' + scope.row.deliverToAccountNumber" placement="top">
            <span> {{ scope.row.deliverToAccountName }} - {{ scope.row.deliverToAccountNumber }} </span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
        prop="totalShipments"
        :label="$t('invoices.numOfShipments')"
        :min-width="8"
        sortable="custom"
        align="center">
      </el-table-column>
      <el-table-column
        prop="numberOfUnits"
        :label="$t('outbound.shuttleLoad.numberOfUnits')"
        :min-width="6"
        sortable="custom"
        align="center">
      </el-table-column>
      <el-table-column
        prop="totalCubes"
        :label="$t('invoices.cubes')"
        :min-width="8"
        sortable="custom"
        align="center">
      </el-table-column>
      <el-table-column
        prop="totalAmount"
        :label="$t('invoices.totalAmount')"
        :min-width="10"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <span>{{ getAmount(scope.row.totalAmount) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="posted"
        :label="$t('invoices.posted')"
        :min-width="7"
        sortable="custom"
        align="center">
        <template slot-scope="scope">
          <el-tooltip v-if="scope.row.posted" class="item" effect="dark" :enterable="false" :content="getIsPostedTooltipLabel(scope.row.posted)" placement="top-start">
            <i class="el-icon-success green-success"></i>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column
          v-if="!invoices.isInBulkPrinting && !invoices.isInUploadToVersapay"
          width="70"
          :label="$t('common.options')"
          align="center"
          class-name="fixed-width">
        <template slot-scope="scope">
          <flower-invoice-detail-dialog :invoice.sync="scope.row" />
          <manual-invoice-detail-dialog :invoice="scope.row" />
          <adjustment-invoice-detail-dialog :invoice="scope.row" />
          <print-invoice v-if="scope.row.active" :id ="scope.row.id" :invoice-type="scope.row.type" :show-type="'Button'"/>
          <el-tooltip
              v-if="!scope.row.posted && scope.row.active && scope.row.type !== flowerInvoiceType && $can(permissions.BILLING.EDIT_INVOICE)"
              class="item"
              effect="dark"
              :enterable="false"
              :content="$t('common.delete')"
              placement="top-start">
            <i class="el-icon-delete icon-action" @click="handleDelete(scope.row)"></i>
          </el-tooltip>
        </template>
      </el-table-column>
       <!-- V-if does not works correctly in dynamic column of type selection, we need to do a little hack with v-for -->
      <el-table-column
        v-for="i in (invoices.isInBulkPrinting || invoices.isInUploadToVersapay)? [1]: []" :key="i"
        type="selection"
        align="center"
        width="70"
      />
    </el-table>
    <div class="pagination-footer" v-show="invoices.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title"> {{ $t('common.total') }}</span>
           {{ invoices.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="invoices.actualFilters.rows"
        :current-page.sync="invoices.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="invoices.total"
        layout="sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import constants from '@/utils/constants';
import PrintInvoice from '@/views/customerService/components/PrintInvoice';
import { INVOICES_SEARCH } from '@/store/actions';
import FlowerInvoiceDetailDialog from './components/FlowerInvoiceDetailDialog';
import ManualInvoiceDetailDialog from './components/ManualInvoiceDetailDialog';
import AdjustmentInvoiceDetailDialog from './components/AdjustmentInvoiceDetailDialog';

export default {
  name: 'InvoicesSummarySection',
  components: {
    FlowerInvoiceDetailDialog, ManualInvoiceDetailDialog, AdjustmentInvoiceDetailDialog, PrintInvoice
  },
  computed: {
    ...mapGetters(['invoices']),
    invoiceStatusOptions() {
      return constants.INVOICE.TYPES;
    },
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    },
    dateDisplayFormat() {
      return constants.DATES.DEFAULT_DISPLAY_FORMAT;
    },
    flowerInvoiceType() {
      return constants.INVOICE.TYPES[0].value;
    },
  },
  watch: {
    'invoices.list': function wacthInvoiceList() {
      if (this.invoices.isInBulkPrinting || this.invoices.isInUploadToVersapay) {
        let listToCheck;
        if (this.invoices.selectAllChecked) {
          listToCheck = this.invoices.list.filter(invoiceLocal => !this.invoices.selectedIdsExcluded.includes(invoiceLocal.id));
        } else {
          listToCheck = this.invoices.list.filter(invoiceLocal => this.invoices.selectedIdsIncluded.includes(invoiceLocal.id));
        }
        this.$nextTick(() => {
          listToCheck.forEach(row => this.$refs.searchSummaryInvoicesTable.toggleRowSelection(row));
        });
      }
    },
    'invoices.isInBulkPrinting': function wacthInvoiceIsInBulkPrinting() {
      this.$refs.searchSummaryInvoicesTable.clearSelection();
    },
    'invoices.isInUploadToVersapay': function wacthInvoiceIsInUploadToVersapay() {
      this.$refs.searchSummaryInvoicesTable.clearSelection();
    }
  },
  methods: {
    handleSearchChangeLimit(val) {
      this.$store.dispatch(INVOICES_SEARCH, { rows: val, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(INVOICES_SEARCH, { page: val });
    },
    handleSortChange({ prop, order }) {
      this.$store.dispatch(INVOICES_SEARCH, {
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    },
    async handleDelete(row) {
      const confirm = await this.$confirm(
        this.$t('invoices.confirmDelete')
          .replace('[invoiceNumber]', row.number)
          .replace('[date]', moment(row.date).format(this.dateDisplayFormat))
          .replace('[amount]', this.$options.filters.currency(row.totalAmount))
          .replace('[billTo]', `${row.billToAccountName} - ${row.billToAccountNumber}`),
        { confirmButtonText: this.$t('common.delete'), dangerouslyUseHTMLString: true }
      ).catch(() => {
      });

      if (confirm) {
        const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
        try {
          const response = await this.$store.dispatch('invoices/delete', row.id);
          this.$message.success(response.message);
        } finally {
          loading.close();
          await this.$store.dispatch(INVOICES_SEARCH, {});
        }
      }
    },
    getNameOfInvoiceType(numType) {
      return constants.INVOICE.TYPES.find(type => type.value === numType).label;
    },
    getAmount(amount) {
      if (amount < 0) {
        return `(${this.$options.filters.currency(amount).replace('-', '')})`;
      }
      return this.$options.filters.currency(amount);
    },
    getIsPostedTooltipLabel(posted) {
      return posted ? constants.STATUS_BOOLEAN.yes.label : constants.STATUS_BOOLEAN.no.label;
    },
    handleSelectAllToBulkPrint(selection) {
      this.$store.dispatch('invoices/toggleBulkPrintAllChecked', selection && selection.length > 0);
    },
    handleSelectSingleToBulkPrint(selection, row) {
      this.$store.dispatch('invoices/toggleBulkPrintSingleCheck', row.id);
    }
  },
  beforeDestroy() {
    this.$store.dispatch('invoices/clearList');
  }
};
</script>
